/**
 * A code repository linked to a project (card 5c626304, owner 28.09.2026): a
 * REFERENCE only — a name, where it lives (a remote URL or a local clone
 * folder), the provider for the icon, an optional default branch and note.
 * Never a token: a URL carrying a user or password is cleaned (user) or
 * refused (password), and a query string or fragment is dropped — a token-like
 * query key (`?private_token=…`) is refused outright (MCP audit card 5c626304,
 * 04.10.2026). Pure — the graph and the MCP graph tools reach this.
 */
import { normalizeRemoteUrl, ownerRepoOf } from '../services/git/providers/remote';

const SCP_LIKE = /^([\w.-]+)@([\w.-]+):(.+)$/;

const PROVIDERS = Object.freeze({
  'github.com': 'github',
  'gitlab.com': 'gitlab',
  'bitbucket.org': 'bitbucket',
  'codeberg.org': 'codeberg',
});

/** The host of a remote URL (https / ssh / user@host:path), lower case; '' when there is none. */
export function hostOf(remote) {
  const scp = SCP_LIKE.exec(String(remote || ''));
  if (scp) return scp[2].toLowerCase();
  try {
    return new URL(remote).hostname.toLowerCase();
  } catch {
    return '';
  }
}

export const providerOfHost = (host) => PROVIDERS[host] || (host.includes('gitlab') ? 'gitlab' : host ? 'git' : 'local');

/** Everything from the first `?` or `#` on is cut: a remote never needs a query or a fragment. */
const withoutQuery = (u) => u.replace(/[?#].*$/s, '');

/**
 * A remote without any user, query or fragment in it ("https://me@github.com/x?private_token=t"
 * → "https://github.com/x"). ssh remotes keep their user: "git@" is the
 * protocol's login, not an account, and a key — never a password — goes with
 * it. The query and fragment go on every scheme: that is where a token hides
 * (`?private_token=`, `?access_token=`), and this function guards both what is
 * stored and what the MCP graph tools return.
 */
export function withoutUser(remote) {
  const u = withoutQuery(String(remote || '').trim());
  if (SCP_LIKE.test(u)) return u;
  try {
    const url = new URL(u);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return u;
    url.username = '';
    url.password = '';
    url.search = '';
    url.hash = '';
    return url.toString().replace(/\/$/, '');
  } catch {
    return u;
  }
}

// A query key that names a credential: token, private_token, access_token,
// oauth_token, api_key, apikey, key, secret, password, pass, pwd, auth, sig…
const TOKEN_KEY = /(token|secret|pass(word)?|pwd|key|auth|sig(nature)?|credential|bearer)/i;

/** The query keys of a remote URL (http(s), ssh:// or scp-like); [] when it has none. */
function queryKeysOf(remote) {
  const q = /\?([^#]*)/.exec(String(remote || ''));
  if (!q) return [];
  return q[1]
    .split('&')
    .map((pair) => {
      const raw = pair.split('=')[0];
      try {
        return decodeURIComponent(raw.replace(/\+/g, ' ')).trim();
      } catch {
        return raw.trim();
      }
    })
    .filter(Boolean);
}

/** True when a remote URL carries a token-like query key (`?private_token=…`). */
export function hasTokenInQuery(remote) {
  return queryKeysOf(remote).some((key) => TOKEN_KEY.test(key));
}

/**
 * The page of a remote repository in a browser: <origin>/owner/repo (".git"
 * dropped). An http(s) remote keeps its own scheme and port (a self-hosted
 * server); an ssh remote's page is assumed at https://host.
 */
export function webUrlOf(remote) {
  const host = hostOf(remote);
  if (!host) return '';
  let origin = `https://${host}`;
  try {
    const url = new URL(remote);
    if (url.protocol === 'http:' || url.protocol === 'https:') origin = url.origin;
  } catch {
    /* scp-like: keep https://host */
  }
  const { owner, repo } = ownerRepoOf(remote);
  const path = [owner, repo].filter(Boolean).join('/');
  return path ? `${origin}/${path}` : origin;
}

const lastSegment = (p) => String(p || '').replace(/[\\/]+$/, '').split(/[\\/]/).pop() || '';

/**
 * What the user typed → a repo reference (throws a readable message when it
 * cannot be one). `url` and `path` are alternatives: a remote, or a local clone.
 */
export function repoRefFromInput({ url = '', path = '', name = '', branch = '', description = '' } = {}) {
  const trimmedPath = String(path || '').trim();
  let remote = '';
  if (String(url || '').trim()) {
    const normalized = normalizeRemoteUrl(url);
    if (hasTokenInQuery(normalized)) {
      throw new Error('Keep the token out of the URL — remove the ?…token=… part and choose a Vault item for the credential instead.');
    }
    remote = withoutUser(normalized);
  }
  else if (!trimmedPath) throw new Error('Paste the repository URL, or choose its folder.');
  const host = remote ? hostOf(remote) : '';
  const fromUrl = remote ? ownerRepoOf(remote).repo : '';
  return {
    name: String(name || '').trim() || fromUrl || lastSegment(trimmedPath) || 'Repository',
    remote,
    path: remote ? '' : trimmedPath,
    provider: remote ? providerOfHost(host) : 'local',
    host,
    branch: String(branch || '').trim().slice(0, 200),
    description: String(description || '').trim().slice(0, 2000),
  };
}
